import {
  ActionMeta,
  GroupBase,
  MenuListProps,
  OnChangeValue,
  OptionsOrGroups,
  components,
} from "react-select";
import { SelectItem, CustomSelectProps } from "../Select";
import { useCustomTranslation } from "../../../helpers/translateHelpers";

// Handle select all / deselect all is based on issues and chatgpt

const isGroup = (
  entry: SelectItem | GroupBase<SelectItem>,
): entry is GroupBase<SelectItem> => "options" in entry;

const flattenOptions = (
  input: OptionsOrGroups<SelectItem, GroupBase<SelectItem>>,
): SelectItem[] => {
  const flat: SelectItem[] = [];
  input.forEach((entry) => {
    if (isGroup(entry)) {
      flat.push(...entry.options);
    } else {
      flat.push(entry);
    }
  });
  return flat;
};

const MenuList = (
  props: MenuListProps<SelectItem, false, GroupBase<SelectItem>>,
) => {
  const { tInput } = useCustomTranslation();

  const { newOption, menuListFooter, onChange, name } =
    props.selectProps as CustomSelectProps;

  const isMulti = !!props.selectProps.isMulti;
  const visible = flattenOptions(props.options);
  const selected = (props.getValue?.() as SelectItem[]) ?? [];
  const isAllSelected =
    visible.length > 0 &&
    visible.every((o) => selected.some((s) => s.value === o.value));

  const emitChange = (
    value: OnChangeValue<SelectItem, boolean>,
    action: ActionMeta<SelectItem>,
  ) => onChange?.(value, action);

  const handleToggleAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMulti) return;

    if (isAllSelected) {
      const next = selected.filter(
        (s) => !visible.some((o) => o.value === s.value),
      );
      emitChange(next, {
        action: "deselect-option",
        name,
      } as ActionMeta<SelectItem>);
    } else {
      const next = [
        ...selected,
        ...visible.filter((o) => !selected.some((s) => s.value === o.value)),
      ];
      emitChange(next, {
        action: "select-option",
        name,
      } as ActionMeta<SelectItem>);
    }
  };

  return (
    <components.MenuList {...props}>
      {isMulti && (
        <div
          className={`text-dark my-2.5 flex cursor-pointer flex-row items-center gap-2 rounded-[4px] p-2 text-[14px]/[20px] font-medium tracking-[-0.14px]`}
          onMouseDown={handleToggleAll}
        >
          {isAllSelected ? tInput("deselect_all") : tInput("select_all")}
        </div>
      )}
      {newOption && (
        <div
          className={`bg-gray-accent/30 text-primary my-2.5 flex cursor-pointer flex-row items-center gap-2 rounded-[4px] p-2 text-[14px]/[20px] font-medium tracking-[-0.14px]`}
          onMouseDown={(e) => {
            e.preventDefault();
            newOption.onClick();
          }}
        >
          {newOption.label}
        </div>
      )}
      {props.children}
      {menuListFooter ? menuListFooter : null}
    </components.MenuList>
  );
};

export default MenuList;
